import type { SavedProject } from '../types';

export const CURRENT_SCHEMA_VERSION = 1;

/**
 * Registry of migration functions. Each key migrates FROM that version TO version+1.
 *
 * To add a migration:
 * 1. Bump CURRENT_SCHEMA_VERSION
 * 2. Add entry: { [oldVersion]: (saved) => ({ ...saved, schemaVersion: oldVersion + 1, project: { ... } }) }
 *
 * Example for v1 → v2:
 *   1: (saved) => ({
 *     ...saved,
 *     schemaVersion: 2,
 *     project: { ...saved.project, newField: 'default' },
 *   })
 */
const migrations: Record<number, (saved: SavedProject) => SavedProject> = {};

/**
 * Step-through migration: runs each migration in sequence until current version.
 * Returns a project at CURRENT_SCHEMA_VERSION, ready for use.
 *
 * Guarantees:
 * - Projects at current version pass through unchanged
 * - Future versions (higher than current) throw — prevents data corruption from downgrades
 * - Missing migration steps log a warning and jump to current (best-effort recovery)
 * - Each migration step must increment schemaVersion by exactly 1
 */
export function migrateProject(saved: SavedProject): SavedProject {
  // Validate input
  if (!saved || typeof saved.schemaVersion !== 'number') {
    console.warn('Invalid saved project — missing schemaVersion, treating as v1');
    return { ...saved, schemaVersion: CURRENT_SCHEMA_VERSION };
  }

  // Already current
  if (saved.schemaVersion === CURRENT_SCHEMA_VERSION) {
    return saved;
  }

  // Future version — don't corrupt data by downgrading
  if (saved.schemaVersion > CURRENT_SCHEMA_VERSION) {
    throw new Error(
      `Project schema version ${saved.schemaVersion} is newer than supported version ${CURRENT_SCHEMA_VERSION}. ` +
      `Please update the application.`
    );
  }

  // Step-through migration
  let current = saved;
  while (current.schemaVersion < CURRENT_SCHEMA_VERSION) {
    const fromVersion = current.schemaVersion;
    const migrateFn = migrations[fromVersion];

    if (!migrateFn) {
      console.warn(
        `No migration found for schema version ${fromVersion} → ${fromVersion + 1}. ` +
        `Jumping to current version ${CURRENT_SCHEMA_VERSION} (data may be incomplete).`
      );
      current = { ...current, schemaVersion: CURRENT_SCHEMA_VERSION };
      break;
    }

    try {
      current = migrateFn(current);
    } catch (err) {
      console.error(`Migration from v${fromVersion} failed:`, err);
      throw new Error(`Failed to migrate project from schema v${fromVersion}: ${err}`);
    }

    // Safety: ensure migration actually incremented the version
    if (current.schemaVersion !== fromVersion + 1) {
      console.warn(
        `Migration from v${fromVersion} produced v${current.schemaVersion} instead of v${fromVersion + 1}. Fixing.`
      );
      current = { ...current, schemaVersion: fromVersion + 1 };
    }
  }

  return current;
}
